import { css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { customElement } from "lit/decorators/custom-element.js";
import { createRef, ref } from "lit/directives/ref.js";
import { BaseElement } from "./BaseElement";
import { IconSmallSearch } from "./icons";
import { map } from "lit/directives/map.js";
import { classMap } from "lit/directives/class-map.js";
import "./search";
import { ListingType } from "./search";
import * as appState from "./ac101-app";
import { Result } from "./ac101-app";
import { Tooltip } from "bootstrap";

@customElement("ac101-operators-input")
export class AC101OperatorsInput extends BaseElement {
  #debounce: ReturnType<typeof setTimeout> | undefined;
  #abortController: AbortController | undefined;
  static get styles() {
    return css`
      :host {
        display: block;
        width: 100%;
      }

      .suggestions {
        position: relative;
      }

      .suggestions-list {
        position: absolute;
        width: 100%;
        background: white;
        color: var(--bs-blue-primary);
        z-index: 2;

        border-radius: var(--bs-border-radius);
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }

      .suggestion {
        padding: 8px 12px;
        border: 1px solid var(--bs-border-color);
        border-top: none;
        cursor: pointer;
        font-weight: var(--bs-font-weight-medium);
      }
      .suggestion:hover {
        background: var(--bs-green-tertiary);
      }

      .suggestion:last-of-type {
        border-radius: var(--bs-border-radius);
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }

      .selected {
        background: var(--bs-off-white-primary);
      }
    `;
  }
  static get formAssociated() {
    return true;
  }

  #form: HTMLFormElement;
  formAssociatedCallback(form: HTMLFormElement) {
    this.#form = form;
    form.addEventListener("submit", (e) => {
      if (!form.getAttribute("action")) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }
  #searching: Promise<unknown> | undefined;
  #tooltip: Tooltip;

  @property({ type: Boolean }) autofocus = false;

  firstUpdated() {
    if (this.autofocus) {
      requestAnimationFrame(() => {
        this._inputRef.value.focus();
      });
    }

    this.#tooltip = new Tooltip(this._buttonRef.value, {
      placement: "top",
      title: "Please enter an operator name",
      customClass: "ac101-tooltip",
      html: true,
    });
    this.#tooltip.disable();
  }

  _onInput = (e: KeyboardEvent) => {
    const input = e.target as HTMLInputElement;
    this._updateTerm(input.value);
  };

  _updateTerm(term: string) {
    this.term = term;
    if (term) {
      this._cancelSearch();
      this.#debounce = setTimeout(() => {
        this.#searching = this._startSearch(term, "aged-care");
      }, 400);
    }
  }

  _cancelSearch() {
    clearTimeout(this.#debounce);
    this.#abortController?.abort();
  }

  async _onBlur() {
    setTimeout(() => {
      this._suggestions = [];
    }, 200);
  }

  _onFocus = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input.value) {
      this.#searching = this._startSearch(input.value, "aged-care");
    }
  };

  _startSearch = async (term: string, type: ListingType) => {
    this.#abortController = new AbortController();

    const results = await fetch(`/api/search?term=${term}&type=${type}`, {
      signal: this.#abortController.signal,
    });
    const suggestions = await results.json();

    this._suggestions = suggestions.results.filter(
      (x) => x.type === "Operator"
    );
    this._suggestion =
      this._suggestions.length > 0 ? this._suggestions[0].name : undefined;
  };

  _onArrows = (e: KeyboardEvent) => {
    if (this._suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        this._index++;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        this._index--;
      }
      if (this._index < 0) {
        this._index = this._suggestions.length - 1;
      }
      if (this._index > this._suggestions.length - 1) {
        this._index = 0;
      }
    }
    return true;
  };

  _onKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      console.log(this);
      const element = e.target as HTMLElement;
      e.preventDefault();
      e.stopPropagation();
      appState.result.set(this._suggestions[this._index]);
      this.term = appState.result.get().name;
      this._redirectForm();
      element.blur();
    }
    return true;
  };
  _redirectForm() {
    this.#form.action = appState.action.get();
  }
  _select = (e: Event, result: Result) => {
    appState.result.set(result);
    this.term = appState.result.get().name;
    this._redirectForm();
    this._inputRef.value.blur();
  };

  _suggestionClick(e: Event) {
    const { value } = e.target as unknown as { value: Result };
    console.log(e, value);
    e.stopPropagation();
    e.stopImmediatePropagation();
    e.preventDefault();
    this._select(e, value);
  }

  renderSuggestions() {
    console.log("render", this._suggestions);
    return html`<div class="suggestions" @click=${this._suggestionClick}>
      <div class="suggestions-list shadow">
        ${map(
          this._suggestions ?? [],

          (x, index) => html`<div
            @click=${console.log}
            class="${classMap({
              suggestion: true,
              "list-text": true,
              selected: this._index === index,
            })}"
            data-index=${index}
            .value=${x}
          >
            ${x.name}
          </div>`
        )}
      </div>
    </div>`;
  }

  _inputRef = createRef<HTMLInputElement>();
  _buttonRef = createRef<HTMLButtonElement>();

  @state() _suggestions: Result[] = [];

  @state() _suggestion = "";

  @state() _selected = undefined;

  @state() _index = -1;

  @property() term = "";

  #submit(e: Event) {
    if (!this.#form.getAttribute("action")) {
      this.#tooltip.enable();
      this.#tooltip.show();
    } else {
      this.#tooltip.hide();
      this.#tooltip.disable();
      this.#form.submit();
    }
  }

  render() {
    return html`<div class="row g-2">
      <div class="col-md">
        <div class="form-group me-4">
          <input
            ${ref(this._inputRef)}
            @input=${this._onInput}
            @focus=${this._onFocus}
            @blur=${this._onBlur}
            .value=${this.term ?? ""}
            class="form-control form-control-icon-btn search-input-desktop"
            type="text"
            list=""
            name=""
            id=""
            placeholder="Search by Operator Name"
          />

          ${IconSmallSearch} ${this.renderSuggestions()}
        </div>
      </div>
      <div class=" col-md-auto">
        <button
          ${ref(this._buttonRef)}
          @click=${this.#submit}
          type="submit"
          class="btn w-100 btn-search"
        >
          Search
        </button>
      </div>
    </div>`;
  }
}
